import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import api from '../../http/axiosClient';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import assetService from '../../api/assetService';
import {
  getCategories,
  getLayers,
  getSubcategories
} from '../../services/enhancedTaxonomyService';
import PerformanceMonitorService from '../../services/performanceMonitor';
import { Asset, AssetSearchParams } from '../../types/asset.types';
import {
  CategoryOption,
  LayerOption,
  SubcategoryOption,
} from '../../types/taxonomy.types';
import { getBackendUrl } from '../../utils/environment.config';
import AssetCard from '../asset/AssetCard';
import PaginationControls from '../common/PaginationControls';

interface AssetSearchProps {
  onSearch?: (query: string) => void;
  initialParams?: AssetSearchParams;
  onAssetSelect?: (asset: Asset) => Promise<void>;
  searchParams?: any;
  setSearchParams?: any;
  showLayerFilter?: boolean;
  fullWidth?: boolean;
  hideSearchBox?: boolean;
  hideFilters?: boolean;
  excludeLayers?: string[]; // Layers to exclude from search results (e.g., ['C'] to exclude Composite)
  /** When true, hide View details and Create variant buttons on result cards (e.g. composite component selection). */
  hideCardActions?: boolean;
}

const SEARCH_ENDPOINT_BY_LAYER: Record<string, string> = {
  G: '/assets/song/search-registry',
  S: '/assets/star/search',
  M: '/assets/move/search',
  L: '/assets/look/search',
  W: '/assets/world/search',
  C: '/assets/composite/search',
};

interface ManagementSearchUrlParams {
  backendUrl: string;
  layer: string;
  page: number;
  limit: number;
  assetType?: string;
  categoryCode?: string;
  subcategoryCode?: string;
  humanFriendlyName?: string;
  baseAssetId?: string;
}

export const buildManagementSearchUrl = ({
  backendUrl,
  layer,
  page,
  limit,
  assetType,
  categoryCode,
  subcategoryCode,
  humanFriendlyName,
  baseAssetId,
}: ManagementSearchUrlParams): string => {
  const endpointPath = SEARCH_ENDPOINT_BY_LAYER[layer];
  if (!endpointPath) {
    throw new Error(`Unsupported search layer: ${layer}`);
  }

  const url = new URL(`${backendUrl}${endpointPath}`);
  if (categoryCode) {
    url.searchParams.append('categoryCode', categoryCode);
  }
  if (subcategoryCode) {
    url.searchParams.append('subcategoryCode', subcategoryCode);
  }
  if (humanFriendlyName) {
    url.searchParams.append('humanFriendlyName', humanFriendlyName);
  }
  if (assetType) {
    url.searchParams.append('assetType', assetType);
  }
  if (baseAssetId) {
    url.searchParams.append('baseAssetId', baseAssetId);
  }
  url.searchParams.append('page', String(page));
  url.searchParams.append('limit', String(limit));

  return url.toString();
};

export const extractTaxonomyCodes = (item: any): {
  categoryCode: string;
  subcategoryCode: string;
} => ({
  categoryCode:
    item.categoryCode ?? item.category ?? item.categoryId?.toString() ?? '',
  subcategoryCode:
    item.subcategoryCode ??
    item.subcategory ??
    item.subcategoryId?.toString() ??
    '',
});

export const parseSearchApiResponse = (
  data: any
): { results: any[]; totalCount: number } => {
  // Preferred shape: PaginatedListResponse
  if (data?.results != null && Array.isArray(data.results)) {
    return {
      results: data.results,
      totalCount:
        typeof data.totalCount === 'number' ? data.totalCount : data.results.length,
    };
  }

  // Nested PaginatedListResponse
  if (data?.data?.results != null && Array.isArray(data.data.results)) {
    return {
      results: data.data.results,
      totalCount:
        typeof data.data.totalCount === 'number'
          ? data.data.totalCount
          : data.data.results.length,
    };
  }

  // Legacy success payload with array data
  if (data?.success && Array.isArray(data.data)) {
    return { results: data.data, totalCount: data.data.length };
  }

  // Legacy success payload with paginated items
  if (data?.success && data?.data?.items && Array.isArray(data.data.items)) {
    return {
      results: data.data.items,
      totalCount:
        typeof data.data.total === 'number' ? data.data.total : data.data.items.length,
    };
  }

  // Legacy items response
  if (data?.items && Array.isArray(data.items)) {
    return { results: data.items, totalCount: data.items.length };
  }

  // Direct array response
  if (Array.isArray(data)) {
    return { results: data, totalCount: data.length };
  }

  // Edge case: non-success wrapper with array in data
  if (data?.data && Array.isArray(data.data)) {
    return { results: data.data, totalCount: data.data.length };
  }

  return { results: [], totalCount: 0 };
};

const AssetSearch: React.FC<AssetSearchProps> = ({
  onSearch,
  initialParams = {},
  onAssetSelect,
  searchParams,
  setSearchParams,
  showLayerFilter = false,
  fullWidth = false,
  hideSearchBox = false,
  hideFilters = false,
  excludeLayers = [],
  hideCardActions = false,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<Asset[]>([]);
  const [selectedLayer, setSelectedLayer] = useState<string>('G'); // Default to Songs layer
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('');
  const [selectedAssetType, setSelectedAssetType] = useState<string>('all');
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [subcategories, setSubcategories] = useState<SubcategoryOption[]>([]);
  const [layers, setLayers] = useState<LayerOption[]>([]);
  const [totalAssets, setTotalAssets] = useState<number>(0);

  // NEW: Gender filter state for Songs layer
  const [genderFilters, setGenderFilters] = useState<{
    originalArtistGender: string;
    genderSuitability: string;
    vocalRange: string;
    ageAppropriateness: string;
  }>({
    originalArtistGender: '',
    genderSuitability: '',
    vocalRange: '',
    ageAppropriateness: '',
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(12);
  const [totalPages, setTotalPages] = useState<number>(1);

  // Sorting state (fixed to createdAt desc; no UI to change)
  const [sortBy] = useState<string>('createdAt');
  const [sortOrder] = useState<'asc' | 'desc'>('desc');
  const [initialLoad, setInitialLoad] = useState(true);

  // Removed cache busting state - no caching, single direct API calls only

  // Enhanced search features
  const [searchTerms, setSearchTerms] = useState<string[]>([]);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([]);
  // Settings-based filtering (moved from toggle to Settings page)
  const [hideAssetsBeforeDate, setHideAssetsBeforeDate] =
    useState<string>();
  const [isFilterEnabled, setIsFilterEnabled] = useState<boolean>(true);

  // Enhanced search state for HFN/MFA detection
  const [searchType, setSearchType] = useState<'HFN' | 'MFA' | 'GENERAL'>(
    'GENERAL'
  );
  const [searchMetadata, setSearchMetadata] = useState<{
    query: string;
    searchType: string;
    found: number;
  } | null>(null);
  const [layerUnsupportedMessage, setLayerUnsupportedMessage] = useState<string | null>(null);

  // Debounce timeout ref for automatic search
  const searchDebounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Track if component has mounted to avoid triggering search on initial render
  const hasMountedRef = useRef<boolean>(false);

  const navigate = useNavigate();
  // Layers that support creating variants (same as registration)
  const VARIANT_LAYERS = ['G', 'S', 'L', 'M', 'W'];

  // Helper function to generate dynamic placeholder text
  const getSearchPlaceholder = () => {
    switch (searchType) {
      case 'HFN':
        return 'Enter asset name (e.g., C.FUL.POP.1041)';
      case 'MFA':
        return 'Enter component address (e.g., G.006.003.001)';
      default:
        return 'Search by name, HFN (C.FUL.POP.1041), MFA (G.006.003.001), or keywords...';
    }
  };

  // Helper function to get search type badge color
  const getSearchTypeBadgeColor = () => {
    switch (searchType) {
      case 'HFN':
        return 'success';
      case 'MFA':
        return 'info';
      default:
        return 'default';
    }
  };

  // Helper function to get search type badge label
  const getSearchTypeBadgeLabel = () => {
    switch (searchType) {
      case 'HFN':
        return '🎯 Asset Name Search';
      case 'MFA':
        return '🔍 Address Search';
      default:
        return '📝 General Search';
    }
  };

  // Load layers for filtering
  useEffect(() => {
    const loadLayers = async () => {
      try {
        const layerCodes = await getLayers();

        // Layer names mapping
        const layerNames: Record<string, string> = {
          G: 'Songs',
          S: 'Stars',
          L: 'Looks',
          M: 'Moves',
          W: 'Worlds',
          C: 'Composites',
          B: 'Branded',
          P: 'Personalize',
          T: 'Training_Data',
          R: 'Rights',
        };

        // Transform to LayerOption format expected by AssetSearch
        const availableLayers = layerCodes
          .map(layerCode => ({
          id: layerCode,
          code: layerCode,
          name: layerNames[layerCode] || layerCode,
        }));

        setLayers(availableLayers);
      } catch (error) {
        console.error('Error loading layers:', error);
      }
    };

    loadLayers();
  }, []);

  // CRITICAL FIX: Sync initialParams to state on mount
  useEffect(() => {
    if (initialParams) {
      console.log('🔄 [INITIAL PARAMS SYNC] Syncing initialParams to state:', initialParams);
      // Always set a layer - use initialParams.layer if provided, otherwise keep default 'G'
      setSelectedLayer(initialParams.layer || 'G');
      if (initialParams.category) setSelectedCategory(initialParams.category);
      if (initialParams.subcategory) setSelectedSubcategory(initialParams.subcategory);
      if (initialParams.assetType) {
        console.log(`🎯 [ASSET TYPE SYNC] Setting assetType filter to: "${initialParams.assetType}"`);
        setSelectedAssetType(initialParams.assetType);
      }
    }
  }, []); // Run only on mount

  // CRITICAL FIX: Auto-trigger search after initialParams sync completes
  useEffect(() => {
    // When parent controls search via searchParams (e.g. component browse), only searchParams effect should drive search
    if (searchParams?.layer !== undefined) return;

    // Only trigger if we have initialParams with filters (Browse Base Assets scenario)
    const hasFilters = initialParams && (initialParams.layer || initialParams.category || initialParams.subcategory || initialParams.assetType);

    if (hasFilters) {
      console.log('🔍 [AUTO-SEARCH CHECK] initialParams detected with filters:', initialParams);

      // Check if state has been synced (values match initialParams)
      const isStateSynced =
        (!initialParams.layer || selectedLayer === initialParams.layer) &&
        (!initialParams.category || selectedCategory === initialParams.category) &&
        (!initialParams.subcategory || selectedSubcategory === initialParams.subcategory) &&
        (!initialParams.assetType || selectedAssetType === initialParams.assetType);

      console.log('🔍 [AUTO-SEARCH CHECK] State sync status:', {
        isStateSynced,
        expectedLayer: initialParams.layer,
        actualLayer: selectedLayer,
        expectedCategory: initialParams.category,
        actualCategory: selectedCategory,
        expectedSubcategory: initialParams.subcategory,
        actualSubcategory: selectedSubcategory,
        expectedAssetType: initialParams.assetType,
        actualAssetType: selectedAssetType
      });

      if (isStateSynced) {
        // State is synced, trigger search
        const timer = setTimeout(() => {
          console.log('🔍 [AUTO-SEARCH] State synced - performing filtered search with:', {
            layer: selectedLayer,
            category: selectedCategory,
            subcategory: selectedSubcategory,
            assetType: selectedAssetType
          });
          performSearch(1);
        }, 50);

        return () => clearTimeout(timer);
      } else {
        console.log('⏳ [AUTO-SEARCH] Waiting for state sync to complete...');
      }
    }
  }, [selectedLayer, selectedCategory, selectedSubcategory, selectedAssetType, initialParams]); // Trigger when filters change OR initialParams changes

  // Sync external searchParams with internal state
  useEffect(() => {
    if (searchParams && searchParams.query !== undefined) {
      setSearchQuery(searchParams.query);
    }
    if (searchParams && searchParams.layer !== undefined) {
      setSelectedLayer(searchParams.layer);
    }
  }, [searchParams]);

  // Auto-trigger search when searchParams change (use overrides so first search uses correct layer/query before state flushes)
  useEffect(() => {
    if (searchParams) {
      performSearch(1, { layer: searchParams.layer, query: searchParams.query });
    }
  }, [searchParams?.query, searchParams?.layer]);

  // Load initial assets when component mounts
  useEffect(() => {
    // CRITICAL FIX: Skip initial load if initialParams are provided with filters
    // This prevents showing all assets before initialParams sync completes
    if (initialParams && (initialParams.layer || initialParams.category || initialParams.subcategory || initialParams.assetType)) {
      console.log('⏭️ [INITIAL LOAD] Skipping - initialParams provided with filters, auto-search will handle after sync');
      return;
    }

    // Skip default search when parent controls search via searchParams (e.g. component browse)
    // So only the "Auto-trigger search when searchParams change" effect runs with correct layer
    if (searchParams && searchParams.layer !== undefined) {
      return;
    }

    // Trigger initial search with default layer (G - Songs)
    console.log('🔍 [INITIAL LOAD] Triggering initial search with default layer');
    performSearch(1);
  }, [itemsPerPage]);

  // Load categories when layer changes
  useEffect(() => {
    if (!selectedLayer) {
      setCategories([]);
      return;
    }

    const loadCategories = async () => {
      try {
        const taxonomyCategories = await getCategories(selectedLayer);

        // Transform TaxonomyItem[] to CategoryOption[] format expected by AssetSearch
        const availableCategories = taxonomyCategories.map((category): CategoryOption => {
          const numericId = typeof category.numericCode === 'string'
            ? parseInt(category.numericCode, 10)
            : (typeof category.numericCode === 'number' ? category.numericCode : 0);
          const rawId = (category as { id?: number | string }).id;
          const actualId = rawId !== undefined && rawId !== null ? rawId : (numericId || 0);
          return {
            id: actualId as number,
            code: category.code,
            name: category.name,
            numericCode: category.numericCode,
          } as unknown as CategoryOption;
        });

        setCategories(availableCategories);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };

    loadCategories();
  }, [selectedLayer]);

  // Load subcategories when category changes
  useEffect(() => {
    if (!selectedLayer || !selectedCategory) {
      setSubcategories([]);
      return;
    }

    const loadSubcategories = async () => {
      try {
        const taxonomySubcategories = await getSubcategories(
          selectedLayer,
          selectedCategory
        );

        // Transform TaxonomyItem[] to SubcategoryOption[] format expected by AssetSearch
        const availableSubcategories = taxonomySubcategories.map((subcategory): SubcategoryOption => {
          const numericId = typeof subcategory.numericCode === 'string'
            ? parseInt(subcategory.numericCode, 10)
            : (typeof subcategory.numericCode === 'number' ? subcategory.numericCode : 0);
          const rawId = (subcategory as { id?: number | string }).id;
          const actualId = rawId !== undefined && rawId !== null ? rawId : (numericId || 0);
          return {
            id: actualId as number,
            code: subcategory.code,
            name: subcategory.name,
            numericCode: subcategory.numericCode,
          } as unknown as SubcategoryOption;
        });

        // Debug subcategory loading in development
        if (process.env.NODE_ENV === 'development') {
          console.log(
            `🔍 Loading subcategories for ${selectedLayer} > ${selectedCategory}: found ${
              availableSubcategories?.length || 0
            } items`
          );
          if (
            selectedCategory?.includes('Rock') ||
            selectedCategory?.includes('RCK') ||
            selectedCategory?.includes('Rock_Dance')
          ) {
            console.log('🔍 Rock-related subcategories:', {
              layer: selectedLayer,
              category: selectedCategory,
              subcategories: availableSubcategories,
            });
          }
          if (selectedLayer === 'M' || selectedLayer === 'Moves') {
            console.log('🔍 Moves layer subcategories:', {
              layer: selectedLayer,
              category: selectedCategory,
              subcategories: availableSubcategories,
            });
          }
        }

        setSubcategories(availableSubcategories as SubcategoryOption[]);
      } catch (error) {
        console.error('Error loading subcategories:', error);
        console.error(
          `Failed to load subcategories for layer: ${selectedLayer}, category: ${selectedCategory}`
        );

        // Enhanced debug information for troubleshooting
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 Subcategory loading failed - Debug info:', {
            layer: selectedLayer,
            category: selectedCategory,
            errorDetails:
              error instanceof Error ? error.message : 'Unknown error',
          });
        }
      }
    };

    loadSubcategories();
  }, [selectedLayer, selectedCategory]);

  const performSearch = async (page = currentPage, overrides?: { layer?: string; query?: string }) => {
    setIsLoading(true);

    // Use overrides when provided (e.g. from searchParams) so first search uses correct layer/query before state flushes
    const effectiveLayer = overrides?.layer ?? selectedLayer;
    const effectiveQuery = overrides?.query ?? searchQuery;

    // Start performance monitoring
    const perfTimer = PerformanceMonitorService.startTimer();

    try {
      // ENHANCED SEARCH: Check if this is an HFN/MFA search
      const detectedSearchType = assetService.detectSearchType(
        effectiveQuery || ''
      );
      setSearchType(detectedSearchType);

      const SUPPORTED_SEARCH_LAYERS = ['G', 'S', 'L', 'M', 'W', 'C'];
      if (effectiveLayer && !SUPPORTED_SEARCH_LAYERS.includes(effectiveLayer)) {
        setLayerUnsupportedMessage('Search for this layer will be available soon.');
        setSearchResults([]);
        setTotalAssets(0);
        setCurrentPage(1);
        setTotalPages(1);
        setIsLoading(false);
        perfTimer.stop({
          searchType: 'search',
          query: effectiveQuery,
          layer: effectiveLayer,
          resultCount: 0,
          apiCalls: 0,
          dataTransferred: 0,
          cacheHits: 0,
          cacheMisses: 0,
          clientSideFiltering: false,
          serverSideFiltering: false,
        });
        return;
      }
      setLayerUnsupportedMessage(null);

      // Use 0-based page index for API (UI page is 1-based)
      const effectivePage = page - 1;
      const effectiveLimit = itemsPerPage; // Use picker value (6, 12, 24, 48) for all requests
      const categoryCode = selectedCategory || undefined;
      const subcategoryCode = selectedSubcategory || undefined;
      const humanFriendlyName =
        effectiveQuery && effectiveQuery.trim()
          ? effectiveQuery.trim()
          : undefined;
      const baseAssetId =
        searchParams?.baseAssetId ??
        (initialParams as { baseAssetId?: string })?.baseAssetId;

      // Map UI asset type filter to API AssetTypeFilter enum (BASE | VARIANT); omit when "all"
      const assetTypeParam =
        selectedAssetType === 'all'
          ? undefined
          : selectedAssetType === 'base'
            ? 'BASE'
            : selectedAssetType === 'variant'
              ? 'VARIANT'
              : undefined;

      // NEW: Songs layer uses dedicated song search endpoint
      if (effectiveLayer === 'G') {
        try {
          const songSearchParams: Record<string, string | number> = {
            ...(categoryCode ? { categoryCode } : {}),
            ...(subcategoryCode ? { subcategoryCode } : {}),
            ...(humanFriendlyName ? { humanFriendlyName } : {}),
            ...(assetTypeParam ? { assetType: assetTypeParam } : {}),
            ...(baseAssetId ? { baseAssetId } : {}),
            page: effectivePage,
            limit: effectiveLimit,
          };
          console.log('🎵 [SONG SEARCH] Using song search endpoint with params:', songSearchParams);
          const url = buildManagementSearchUrl({
            backendUrl: getBackendUrl(),
            layer: effectiveLayer,
            categoryCode,
            subcategoryCode,
            humanFriendlyName,
            assetType: assetTypeParam,
            baseAssetId,
            page: effectivePage,
            limit: effectiveLimit,
          });

          console.log('🎵 [SONG SEARCH] URL with params:', url);
          
          const response = await api.get(url, {
            headers: {
              'Content-Type': 'application/json',
            },
          });
          const data = response.data;
          // Parse PaginatedListResponse (results + totalCount) or fallback to array/data
          let songResults: any[] = [];
          let songTotalCount: number | undefined;
          if (data?.results != null && Array.isArray(data.results)) {
            songResults = data.results;
            songTotalCount = typeof data.totalCount === 'number' ? data.totalCount : songResults.length;
          } else if (data?.data?.results != null && Array.isArray(data.data.results)) {
            songResults = data.data.results;
            songTotalCount = typeof data.data.totalCount === 'number' ? data.data.totalCount : songResults.length;
          } else if (Array.isArray(data)) {
            songResults = data;
            songTotalCount = songResults.length;
          } else if (data?.success && Array.isArray(data.data)) {
            songResults = data.data;
            songTotalCount = songResults.length;
          } else if (data?.data && Array.isArray(data.data)) {
            songResults = data.data;
            songTotalCount = songResults.length;
          }

          console.log(`🎵 [SONG SEARCH] Received ${songResults.length} songs` + (songTotalCount != null ? `, totalCount: ${songTotalCount}` : ''));

          // Map song search response to Asset type
          const mappedResults: Asset[] = songResults.map((song: any) => {
            const taxonomyCodes = extractTaxonomyCodes(song);
            return ({
            id: song.id || song._id || '',
            _id: song.id || song._id,
            name: song.songName || song.humanFriendlyName || '',
            friendlyName: song.humanFriendlyName || song.songName || '',
            humanFriendlyName: song.humanFriendlyName ?? '',
            machineFriendlyName: song.humanFriendlyName ?? '',
            description: song.description,
            layer: 'G',
            categoryCode: taxonomyCodes.categoryCode,
            subcategoryCode: taxonomyCodes.subcategoryCode,
            type: 'song',
            imageUrl: song.imageUrl || song.thumbnailUrl || '',
            files: [],
            metadata: {
              songName: song.songName,
              artistName: song.artistName,
              categoryName: song.categoryName,
              subcategoryName: song.subcategoryName,
              categoryId: song.categoryId,
              subcategoryId: song.subcategoryId,
              thumbnailUrl: song.thumbnailUrl,
              audioUrl: song.audioUrl ?? song.fileUrl,
              fileUrl: song.fileUrl,
            },
            createdAt: new Date().toISOString(), // Default if not provided
            updatedAt: new Date().toISOString(), // Default if not provided
            createdBy: '',
            updatedBy: '',
            status: 'active' as const, // Required field - assume active for search results
            songMetadata: {
              songName: song.songName,
              artistName: song.artistName,
            },
            albumArt: song.thumbnailUrl,
            baseAsset: song.baseAsset,
            });
          });

          // Apply settings-based filtering
          let filteredResults = mappedResults;
          if (isFilterEnabled && hideAssetsBeforeDate) {
            const cutoffDate = new Date(`${hideAssetsBeforeDate}T00:00:00Z`);
            filteredResults = mappedResults.filter(asset => {
              try {
                const createdAt = new Date(
                  asset.createdAt ||
                    asset.metadata?.createdAt ||
                    (asset as any).created_at
                );
                return createdAt >= cutoffDate;
              } catch (error) {
                return true; // Fail open
              }
            });
          }

          // Apply client-side sorting
          let sortedResults = filteredResults;
          if (sortBy && sortedResults.length > 0) {
            sortedResults = applySortToResults(sortedResults, sortBy, sortOrder);
          }

          // Handle pagination: use backend totalCount when available (PaginatedListResponse), else result length
          const songBackendTotal = songTotalCount ?? sortedResults.length;
          const isSortingActive = sortBy && sortBy !== '';

          if (isSortingActive && sortedResults.length > itemsPerPage) {
            const startIndex = (page - 1) * itemsPerPage;
            const endIndex = startIndex + itemsPerPage;
            const paginatedResults = sortedResults.slice(startIndex, endIndex);

            setSearchResults(paginatedResults);
            setTotalAssets(songBackendTotal);
            setCurrentPage(page);
            setTotalPages(Math.ceil(songBackendTotal / itemsPerPage));
          } else {
            setSearchResults(sortedResults);
            setTotalAssets(songBackendTotal);
            setCurrentPage(page);
            setTotalPages(Math.ceil(songBackendTotal / itemsPerPage));
          }

          onSearch?.(effectiveQuery);

          // Record performance metrics
          perfTimer.stop({
            searchType: page === 1 ? 'search' : 'pagination',
            query: effectiveQuery,
            layer: effectiveLayer,
            category: selectedCategory,
            subcategory: selectedSubcategory,
            resultCount: sortedResults.length,
            apiCalls: 1,
            dataTransferred: JSON.stringify(sortedResults).length,
            cacheHits: 0,
            cacheMisses: 1,
            clientSideFiltering: false,
            serverSideFiltering: true,
          });

          return; // Exit early for song search
        } catch (error) {
          console.error('❌ [SONG SEARCH] Song search failed:', error);
          throw error;
        }
      }

      // NEW: Stars layer uses dedicated star search endpoint
      if (effectiveLayer === 'S') {
        try {
          const starSearchParams: Record<string, string | number> = {
            ...(categoryCode ? { categoryCode } : {}),
            ...(subcategoryCode ? { subcategoryCode } : {}),
            ...(humanFriendlyName ? { humanFriendlyName } : {}),
            ...(assetTypeParam ? { assetType: assetTypeParam } : {}),
            ...(baseAssetId ? { baseAssetId } : {}),
            page: effectivePage,
            limit: effectiveLimit,
          };

          console.log('⭐ [STAR SEARCH] Using star search endpoint with params:', starSearchParams);
          const url = buildManagementSearchUrl({
            backendUrl: getBackendUrl(),
            layer: effectiveLayer,
            categoryCode,
            subcategoryCode,
            humanFriendlyName,
            assetType: assetTypeParam,
            baseAssetId,
            page: effectivePage,
            limit: effectiveLimit,
          });

          console.log('⭐ [STAR SEARCH] URL with params:', url);
          
          const response = await api.get(url, {
            headers: {
              'Content-Type': 'application/json',
            },
          });
          const data = response.data;
          // Parse PaginatedListResponse (results + totalCount) or fallback to array/data
          let starResults: any[] = [];
          let starTotalCount: number | undefined;
          if (data?.results != null && Array.isArray(data.results)) {
            starResults = data.results;
            starTotalCount = typeof data.totalCount === 'number' ? data.totalCount : starResults.length;
          } else if (data?.data?.results != null && Array.isArray(data.data.results)) {
            starResults = data.data.results;
            starTotalCount = typeof data.data.totalCount === 'number' ? data.data.totalCount : starResults.length;
          } else if (Array.isArray(data)) {
            starResults = data;
            starTotalCount = starResults.length;
          } else if (data?.success && Array.isArray(data.data)) {
            starResults = data.data;
            starTotalCount = starResults.length;
          } else if (data?.data && Array.isArray(data.data)) {
            starResults = data.data;
            starTotalCount = starResults.length;
          }

          console.log(`⭐ [STAR SEARCH] Received ${starResults.length} stars` + (starTotalCount != null ? `, totalCount: ${starTotalCount}` : ''));

          // Map star search response to Asset type
          const mappedResults: Asset[] = starResults.map((star: any) => {
            const taxonomyCodes = extractTaxonomyCodes(star);
            return ({
            id: star.id || star._id || '',
            _id: star.id || star._id,
            name: star.starName || star.humanFriendlyName || '',
            friendlyName: star.humanFriendlyName || star.starName || '',
            humanFriendlyName: star.humanFriendlyName ?? '',
            machineFriendlyName: star.humanFriendlyName ?? '',
            description: star.description || '',
            layer: 'S',
            categoryCode: taxonomyCodes.categoryCode,
            subcategoryCode: taxonomyCodes.subcategoryCode,
            type: 'star',
            imageUrl: star.imageUrl || '',
            files: [],
            metadata: {
              starName: star.starName,
              categoryName: star.categoryName,
              subcategoryName: star.subcategoryName,
              categoryId: star.categoryId,
              subcategoryId: star.subcategoryId,
              imageUrl: star.imageUrl,
              fileUrl: star.fileUrl,
            },
            createdAt: new Date().toISOString(), // Default if not provided
            updatedAt: new Date().toISOString(), // Default if not provided
            createdBy: '',
            updatedBy: '',
            status: 'active' as const, // Required field - assume active for search results
            starMetadata: {
              starName: star.starName,
            },
            albumArt: star.imageUrl,
            baseAsset: star.baseAsset,
            });
          });

          // Apply settings-based filtering
          let filteredResults = mappedResults;
          if (isFilterEnabled && hideAssetsBeforeDate) {
            const cutoffDate = new Date(`${hideAssetsBeforeDate}T00:00:00Z`);
            filteredResults = mappedResults.filter(asset => {
              try {
                const createdAt = new Date(
                  asset.createdAt ||
                    asset.metadata?.createdAt ||
                    (asset as any).created_at
                );
                return createdAt >= cutoffDate;
              } catch (error) {
                return true; // Fail open
              }
            });
          }

          // Apply client-side sorting
          let sortedResults = filteredResults;
          if (sortBy && sortedResults.length > 0) {
            sortedResults = applySortToResults(sortedResults, sortBy, sortOrder);
          }

          // Handle pagination: use backend totalCount when available (PaginatedListResponse), else result length
          const starBackendTotal = starTotalCount ?? sortedResults.length;
          const isSortingActive = sortBy && sortBy !== '';

          if (isSortingActive && sortedResults.length > itemsPerPage) {
            const startIndex = (page - 1) * itemsPerPage;
            const endIndex = startIndex + itemsPerPage;
            const paginatedResults = sortedResults.slice(startIndex, endIndex);

            setSearchResults(paginatedResults);
            setTotalAssets(starBackendTotal);
            setCurrentPage(page);
            setTotalPages(Math.ceil(starBackendTotal / itemsPerPage));
          } else {
            setSearchResults(sortedResults);
            setTotalAssets(starBackendTotal);
            setCurrentPage(page);
            setTotalPages(Math.ceil(starBackendTotal / itemsPerPage));
          }

          onSearch?.(effectiveQuery);

          // Record performance metrics
          perfTimer.stop({
            searchType: page === 1 ? 'search' : 'pagination',
            query: effectiveQuery,
            layer: effectiveLayer,
            category: selectedCategory,
            subcategory: selectedSubcategory,
            resultCount: sortedResults.length,
            apiCalls: 1,
            dataTransferred: JSON.stringify(sortedResults).length,
            cacheHits: 0,
            cacheMisses: 1,
            clientSideFiltering: false,
            serverSideFiltering: true,
          });

          return; // Exit early for star search
        } catch (error) {
          console.error('❌ [STAR SEARCH] Star search failed:', error);
          throw error;
        }
      }

      // Moves layer uses dedicated move search endpoint
      if (effectiveLayer === 'M') {
        try {
          const url = buildManagementSearchUrl({
            backendUrl: getBackendUrl(),
            layer: effectiveLayer,
            categoryCode,
            subcategoryCode,
            humanFriendlyName,
            assetType: assetTypeParam,
            baseAssetId,
            page: effectivePage,
            limit: effectiveLimit,
          });

          const response = await api.get(url, {
            headers: {
              'Content-Type': 'application/json',
            },
          });
          const data = response.data;
          // Parse PaginatedListResponse (results + totalCount) or fallback to array/data
          let moveResults: any[] = [];
          let moveTotalCount: number | undefined;
          if (data?.results != null && Array.isArray(data.results)) {
            moveResults = data.results;
            moveTotalCount = typeof data.totalCount === 'number' ? data.totalCount : moveResults.length;
          } else if (data?.data?.results != null && Array.isArray(data.data.results)) {
            moveResults = data.data.results;
            moveTotalCount = typeof data.data.totalCount === 'number' ? data.data.totalCount : moveResults.length;
          } else if (Array.isArray(data)) {
            moveResults = data;
            moveTotalCount = moveResults.length;
          } else if (data?.data && Array.isArray(data.data)) {
            moveResults = data.data;
            moveTotalCount = moveResults.length;
          } else if (data?.success && Array.isArray(data.data)) {
            moveResults = data.data;
            moveTotalCount = moveResults.length;
          }

          const mappedResults: Asset[] = moveResults.map((item: any) => {
            const taxonomyCodes = extractTaxonomyCodes(item);
            return ({
            id: item.id || item._id || '',
            _id: item.id || item._id,
            name: item.moveName || item.humanFriendlyName || '',
            friendlyName: item.humanFriendlyName || item.moveName || '',
            humanFriendlyName: item.humanFriendlyName ?? '',
            machineFriendlyName: item.humanFriendlyName ?? '',
            description: item.description || '',
            layer: 'M',
            categoryCode: taxonomyCodes.categoryCode,
            subcategoryCode: taxonomyCodes.subcategoryCode,
            type: 'move',
            imageUrl: item.fileUrl || item.videoUrl || item.video_url || '',
            files: [],
            metadata: {
              moveName: item.moveName,
              categoryName: item.categoryName,
              subcategoryName: item.subcategoryName,
              categoryId: item.categoryId,
              subcategoryId: item.subcategoryId,
              videoUrl: item.fileUrl || item.videoUrl || item.video_url || '',
              fileUrl: item.fileUrl,
            },
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            createdBy: '',
            updatedBy: '',
            status: 'active' as const,
            moveMetadata: { moveName: item.moveName },
            albumArt: item.fileUrl || item.videoUrl || item.video_url,
            baseAsset: item.baseAsset,
            });
          });

          let filteredResults = mappedResults;
          if (isFilterEnabled && hideAssetsBeforeDate) {
            const cutoffDate = new Date(`${hideAssetsBeforeDate}T00:00:00Z`);
            filteredResults = mappedResults.filter(asset => {
              try {
                const createdAt = new Date(
                  asset.createdAt ||
                    asset.metadata?.createdAt ||
                    (asset as any).created_at
                );
                return createdAt >= cutoffDate;
              } catch (error) {
                return true;
              }
            });
          }

          let sortedResults = filteredResults;
          if (sortBy && sortedResults.length > 0) {
            sortedResults = applySortToResults(sortedResults, sortBy, sortOrder);
          }

          const moveBackendTotal = moveTotalCount ?? sortedResults.length;
          const isSortingActive = sortBy && sortBy !== '';
          if (isSortingActive && sortedResults.length > itemsPerPage) {
            const startIndex = (page - 1) * itemsPerPage;
            const endIndex = startIndex + itemsPerPage;
            const paginatedResults = sortedResults.slice(startIndex, endIndex);
            setSearchResults(paginatedResults);
            setTotalAssets(moveBackendTotal);
            setCurrentPage(page);
            setTotalPages(Math.ceil(moveBackendTotal / itemsPerPage));
          } else {
            setSearchResults(sortedResults);
            setTotalAssets(moveBackendTotal);
            setCurrentPage(page);
            setTotalPages(Math.ceil(moveBackendTotal / itemsPerPage));
          }

          onSearch?.(effectiveQuery);
          perfTimer.stop({
            searchType: page === 1 ? 'search' : 'pagination',
            query: effectiveQuery,
            layer: effectiveLayer,
            category: selectedCategory,
            subcategory: selectedSubcategory,
            resultCount: sortedResults.length,
            apiCalls: 1,
            dataTransferred: JSON.stringify(sortedResults).length,
            cacheHits: 0,
            cacheMisses: 1,
            clientSideFiltering: false,
            serverSideFiltering: true,
          });
          return;
        } catch (error) {
          console.error('❌ [MOVE SEARCH] Move search failed:', error);
          throw error;
        }
      }

      // Looks layer uses dedicated look search endpoint
      if (effectiveLayer === 'L') {
        try {
          const url = buildManagementSearchUrl({
            backendUrl: getBackendUrl(),
            layer: effectiveLayer,
            categoryCode,
            subcategoryCode,
            humanFriendlyName,
            assetType: assetTypeParam,
            baseAssetId,
            page: effectivePage,
            limit: effectiveLimit,
          });

          const response = await api.get(url, {
            headers: {
              'Content-Type': 'application/json',
            },
          });
          const data = response.data;
          // Parse PaginatedListResponse (results + totalCount) or fallback to array/data
          let lookResults: any[] = [];
          let lookTotalCount: number | undefined;
          if (data?.results != null && Array.isArray(data.results)) {
            lookResults = data.results;
            lookTotalCount = typeof data.totalCount === 'number' ? data.totalCount : lookResults.length;
          } else if (data?.data?.results != null && Array.isArray(data.data.results)) {
            lookResults = data.data.results;
            lookTotalCount = typeof data.data.totalCount === 'number' ? data.data.totalCount : lookResults.length;
          } else if (Array.isArray(data)) {
            lookResults = data;
            lookTotalCount = lookResults.length;
          } else if (data?.data && Array.isArray(data.data)) {
            lookResults = data.data;
            lookTotalCount = lookResults.length;
          } else if (data?.success && Array.isArray(data.data)) {
            lookResults = data.data;
            lookTotalCount = lookResults.length;
          }

          const mappedResults: Asset[] = lookResults.map((item: any) => {
            const taxonomyCodes = extractTaxonomyCodes(item);
            return ({
            id: item.id || item._id || '',
            _id: item.id || item._id,
            name: item.outfitName || item.humanFriendlyName || '',
            friendlyName: item.humanFriendlyName || item.outfitName || '',
            humanFriendlyName: item.humanFriendlyName ?? '',
            machineFriendlyName: item.humanFriendlyName ?? '',
            description: item.description || '',
            layer: 'L',
            categoryCode: taxonomyCodes.categoryCode,
            subcategoryCode: taxonomyCodes.subcategoryCode,
            type: 'look',
            imageUrl: item.imageUrl || '',
            files: [],
            metadata: {
              outfitName: item.outfitName,
              categoryName: item.categoryName,
              subcategoryName: item.subcategoryName,
              categoryId: item.categoryId,
              subcategoryId: item.subcategoryId,
              imageUrl: item.imageUrl,
              fileUrl: item.fileUrl,
            },
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            createdBy: '',
            updatedBy: '',
            status: 'active' as const,
            lookMetadata: { outfitName: item.outfitName },
            albumArt: item.imageUrl,
            baseAsset: item.baseAsset,
            });
          });

          let filteredResults = mappedResults;
          if (isFilterEnabled && hideAssetsBeforeDate) {
            const cutoffDate = new Date(`${hideAssetsBeforeDate}T00:00:00Z`);
            filteredResults = mappedResults.filter(asset => {
              try {
                const createdAt = new Date(
                  asset.createdAt ||
                    asset.metadata?.createdAt ||
                    (asset as any).created_at
                );
                return createdAt >= cutoffDate;
              } catch (error) {
                return true;
              }
            });
          }

          let sortedResults = filteredResults;
          if (sortBy && sortedResults.length > 0) {
            sortedResults = applySortToResults(sortedResults, sortBy, sortOrder);
          }

          const lookBackendTotal = lookTotalCount ?? sortedResults.length;
          const isSortingActive = sortBy && sortBy !== '';
          if (isSortingActive && sortedResults.length > itemsPerPage) {
            const startIndex = (page - 1) * itemsPerPage;
            const endIndex = startIndex + itemsPerPage;
            const paginatedResults = sortedResults.slice(startIndex, endIndex);
            setSearchResults(paginatedResults);
            setTotalAssets(lookBackendTotal);
            setCurrentPage(page);
            setTotalPages(Math.ceil(lookBackendTotal / itemsPerPage));
          } else {
            setSearchResults(sortedResults);
            setTotalAssets(lookBackendTotal);
            setCurrentPage(page);
            setTotalPages(Math.ceil(lookBackendTotal / itemsPerPage));
          }

          onSearch?.(effectiveQuery);
          perfTimer.stop({
            searchType: page === 1 ? 'search' : 'pagination',
            query: effectiveQuery,
            layer: effectiveLayer,
            category: selectedCategory,
            subcategory: selectedSubcategory,
            resultCount: sortedResults.length,
            apiCalls: 1,
            dataTransferred: JSON.stringify(sortedResults).length,
            cacheHits: 0,
            cacheMisses: 1,
            clientSideFiltering: false,
            serverSideFiltering: true,
          });
          return;
        } catch (error) {
          console.error('❌ [LOOK SEARCH] Look search failed:', error);
          throw error;
        }
      }

      // Worlds layer uses dedicated world search endpoint
      if (effectiveLayer === 'W') {
        try {
          const url = buildManagementSearchUrl({
            backendUrl: getBackendUrl(),
            layer: effectiveLayer,
            categoryCode,
            subcategoryCode,
            humanFriendlyName,
            assetType: assetTypeParam,
            baseAssetId,
            page: effectivePage,
            limit: effectiveLimit,
          });

          const response = await api.get(url, {
            headers: {
              'Content-Type': 'application/json',
            },
          });
          const data = response.data;
          // Parse PaginatedListResponse (results + totalCount) or fallback to array/data
          let worldResults: any[] = [];
          let worldTotalCount: number | undefined;
          if (data?.results != null && Array.isArray(data.results)) {
            worldResults = data.results;
            worldTotalCount = typeof data.totalCount === 'number' ? data.totalCount : worldResults.length;
          } else if (data?.data?.results != null && Array.isArray(data.data.results)) {
            worldResults = data.data.results;
            worldTotalCount = typeof data.data.totalCount === 'number' ? data.data.totalCount : worldResults.length;
          } else if (Array.isArray(data)) {
            worldResults = data;
            worldTotalCount = worldResults.length;
          } else if (data?.data && Array.isArray(data.data)) {
            worldResults = data.data;
            worldTotalCount = worldResults.length;
          } else if (data?.success && Array.isArray(data.data)) {
            worldResults = data.data;
            worldTotalCount = worldResults.length;
          }

          const mappedResults: Asset[] = worldResults.map((item: any) => {
            const taxonomyCodes = extractTaxonomyCodes(item);
            const worldFileUrl = item.fileUrl || '';
            return ({
            id: item.id || item._id || '',
            _id: item.id || item._id,
            name: item.worldName || item.humanFriendlyName || '',
            friendlyName: item.humanFriendlyName || item.worldName || '',
            humanFriendlyName: item.humanFriendlyName ?? '',
            machineFriendlyName: item.humanFriendlyName ?? '',
            description: item.description || '',
            layer: 'W',
            categoryCode: taxonomyCodes.categoryCode,
            subcategoryCode: taxonomyCodes.subcategoryCode,
            type: 'world',
            imageUrl: worldFileUrl,
            files: [],
            metadata: {
              worldName: item.worldName,
              categoryName: item.categoryName,
              subcategoryName: item.subcategoryName,
              categoryId: item.categoryId,
              subcategoryId: item.subcategoryId,
              fileUrl: worldFileUrl,
            },
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            createdBy: '',
            updatedBy: '',
            status: 'active' as const,
            worldMetadata: { worldName: item.worldName },
            albumArt: worldFileUrl,
            baseAsset: item.baseAsset,
            });
          });

          let filteredResults = mappedResults;
          if (isFilterEnabled && hideAssetsBeforeDate) {
            const cutoffDate = new Date(`${hideAssetsBeforeDate}T00:00:00Z`);
            filteredResults = mappedResults.filter(asset => {
              try {
                const createdAt = new Date(
                  asset.createdAt ||
                    asset.metadata?.createdAt ||
                    (asset as any).created_at
                );
                return createdAt >= cutoffDate;
              } catch (error) {
                return true;
              }
            });
          }

          let sortedResults = filteredResults;
          if (sortBy && sortedResults.length > 0) {
            sortedResults = applySortToResults(sortedResults, sortBy, sortOrder);
          }

          const worldBackendTotal = worldTotalCount ?? sortedResults.length;
          const isSortingActive = sortBy && sortBy !== '';
          if (isSortingActive && sortedResults.length > itemsPerPage) {
            const startIndex = (page - 1) * itemsPerPage;
            const endIndex = startIndex + itemsPerPage;
            const paginatedResults = sortedResults.slice(startIndex, endIndex);
            setSearchResults(paginatedResults);
            setTotalAssets(worldBackendTotal);
            setCurrentPage(page);
            setTotalPages(Math.ceil(worldBackendTotal / itemsPerPage));
          } else {
            setSearchResults(sortedResults);
            setTotalAssets(worldBackendTotal);
            setCurrentPage(page);
            setTotalPages(Math.ceil(worldBackendTotal / itemsPerPage));
          }

          onSearch?.(effectiveQuery);
          perfTimer.stop({
            searchType: page === 1 ? 'search' : 'pagination',
            query: effectiveQuery,
            layer: effectiveLayer,
            category: selectedCategory,
            subcategory: selectedSubcategory,
            resultCount: sortedResults.length,
            apiCalls: 1,
            dataTransferred: JSON.stringify(sortedResults).length,
            cacheHits: 0,
            cacheMisses: 1,
            clientSideFiltering: false,
            serverSideFiltering: true,
          });
          return;
        } catch (error) {
          console.error('❌ [WORLD SEARCH] World search failed:', error);
          throw error;
        }
      }

      // When any sorting is applied, fetch all results for proper client-side sorting (effectivePage/effectiveLimit already set above)
      const isSortingActive = sortBy && sortBy !== ''; // Any sort selection means we need all results

      const fallbackSearchParams: any = {
        humanFriendlyName,
        categoryCode,
        subcategoryCode,
        assetType: assetTypeParam,
        page: effectivePage,
        limit: effectiveLimit,
        ...(baseAssetId ? { baseAssetId } : {}),
        // NEW: Songs Layer Gender Filters
        ...(effectiveLayer === 'G' && {
          originalArtistGender: genderFilters.originalArtistGender || undefined,
          genderSuitability: genderFilters.genderSuitability || undefined,
          vocalRange: genderFilters.vocalRange || undefined,
          ...(genderFilters.ageAppropriateness && {
            ageAppropriateness: genderFilters.ageAppropriateness,
          }),
        }),
        // STEP 1.6 FIX: Removed all cache busting parameters (_t, _v, _fresh)
        // Backend rejects these with "property should not exist" errors
      };

      // CRITICAL FIX 4: Enhanced parameter logging to debug backend filtering
      // Temporarily always log to debug gender filter issue
      console.log(
        '🔍 Search parameters (aligned with backend API):',
        fallbackSearchParams
      );
      console.log('🔍 [BACKEND DEBUG] Raw filter values:', {
        selectedLayer: effectiveLayer,
        selectedCategory,
        selectedSubcategory,
        selectedAssetType,
        categoryCode,
        subcategoryCode,
      });

      // CRITICAL DEBUG: Log assetType filter specifically for Browse Base Assets dialog
      if (selectedAssetType && selectedAssetType !== 'all') {
        console.log(`🔍 [ASSET TYPE FILTER] Filtering by assetType: "${selectedAssetType}" (should exclude ${selectedAssetType === 'base' ? 'variants' : 'base assets'})`);
      }

      // ENHANCED DEBUG: Show gender filter details when layer G is selected
      if (effectiveLayer === 'G') {
        console.log(
          '🎵 [GENDER FILTER DEBUG] Current gender filter state:',
          genderFilters
        );
        console.log(
          '🎵 [GENDER FILTER DEBUG] Parameters being sent to backend:',
          {
            originalArtistGender: fallbackSearchParams.originalArtistGender,
            genderSuitability: fallbackSearchParams.genderSuitability,
            vocalRange: fallbackSearchParams.vocalRange,
            ageAppropriateness: fallbackSearchParams.ageAppropriateness,
          }
        );
      }

      // Always log category/subcategory requests for debugging backend filtering issues
      if (selectedCategory || selectedSubcategory) {
        console.log(
          `🎯 [BACKEND FILTER] Requesting: Layer="${effectiveLayer}", categoryCode="${categoryCode}", subcategoryCode="${subcategoryCode}"`
        );
      }

      const layerPathMap: Record<string, string> = {
        G: 'song',
        S: 'star',
        L: 'look',
        M: 'move',
        W: 'world',
        C: 'composite',
      };
      const layerPath = effectiveLayer ? layerPathMap[effectiveLayer] : undefined;
      if (!layerPath) {
        throw new Error('Please select a supported layer to search.');
      }

      const response = await api.get(`${getBackendUrl()}/assets/${layerPath}/search`, {
        params: fallbackSearchParams,
        timeout: 10000,
      });

      // DEBUG: Log the raw response to understand what backend is returning
      console.log('🔍 [SEARCH DEBUG] Raw API response:', response.data);
      console.log('🔍 [SEARCH DEBUG] Response type:', typeof response.data);
      console.log(
        '🔍 [SEARCH DEBUG] Response keys:',
        Object.keys(response.data || {})
      );
      console.log('🔍 [SEARCH DEBUG] Response status:', response.status);
      console.log('🔍 [SEARCH DEBUG] Search query was:', effectiveQuery);

      // Parse response according to backend API formats (preferred: { results, totalCount })
      const parsedResponse = parseSearchApiResponse(response.data);
      const results: Asset[] = parsedResponse.results;
      const totalCount = parsedResponse.totalCount;

      if (
        process.env.NODE_ENV === 'development' &&
        results.length === 0 &&
        !Array.isArray(response.data) &&
        !(response.data?.results && Array.isArray(response.data.results)) &&
        !(response.data?.data?.results && Array.isArray(response.data.data.results)) &&
        !(response.data?.items && Array.isArray(response.data.items)) &&
        !(response.data?.success && Array.isArray(response.data?.data))
      ) {
        console.warn('⚠️ Unexpected API response format:', response.data);
      }

      // Log asset count for search results
      if (process.env.NODE_ENV === 'development') {
        console.log(
          `🎯 Retrieved ${results.length} assets, total: ${totalCount}`
        );
        if (results.length === 0 && effectiveQuery) {
          console.warn(
            `⚠️ [SEARCH DEBUG] No results for query "${effectiveQuery}" - this might be a backend indexing issue`
          );
        }
      }

      // Debug backend filtering behavior - ALWAYS show for gender filters debugging
      const hasGenderFilters =
        effectiveLayer === 'G' &&
        (genderFilters.originalArtistGender ||
          genderFilters.genderSuitability ||
          genderFilters.vocalRange ||
          genderFilters.ageAppropriateness);

      // ENHANCED DEBUG: Show actual gender filter values and results
      if (hasGenderFilters) {
        console.log(
          `🎵 [GENDER FILTER RESULTS] Layer="${effectiveLayer}", categoryCode="${categoryCode}", subcategoryCode="${subcategoryCode}" → ${
            results.length
          } results`
        );
        console.log(`🎵 [GENDER FILTER REQUEST] Sent to backend:`, {
          originalArtistGender: genderFilters.originalArtistGender || 'NOT SET',
          genderSuitability: genderFilters.genderSuitability || 'NOT SET',
          vocalRange: genderFilters.vocalRange || 'NOT SET',
          ageAppropriateness: genderFilters.ageAppropriateness || 'NOT SET',
        });

        // Show actual gender values in returned assets
        console.log(
          `🎵 [GENDER FILTER RESPONSE] Returned ${results.length} assets with gender data:`
        );
        results.forEach(asset => {
          const genderData = asset.songMetadata || {};
          console.log(
            `   📀 ${asset.name}: gender="${
              genderData.originalArtistGender || 'MISSING'
            }", suitability="${
              genderData.genderSuitability || 'MISSING'
            }", vocal="${genderData.vocalRange || 'MISSING'}", age="${
              genderData.ageAppropriateness || 'MISSING'
            }"`
          );
        });
      }

      // DEBUG: When Songs layer is selected but NO gender filter, show all gender metadata
      if (effectiveLayer === 'G' && !hasGenderFilters && selectedSubcategory) {
        console.log(
          `🎵 [GENDER DEBUG] All ${results.length} assets in G.${categoryCode}.${subcategoryCode} with their gender metadata:`
        );
        results.forEach(asset => {
          const genderData = asset.songMetadata || {};
          console.log(
            `   📀 ${asset.name}: gender="${
              genderData.originalArtistGender || 'MISSING'
            }"`
          );
        });
      }

      // DEBUG: When subcategory filtering returns 0 results, show what we expect to find
      if (selectedSubcategory && results.length === 0) {
        console.log(
          `🔍 [SUBCATEGORY DEBUG] Expected to find assets in ${effectiveLayer}.${categoryCode}.${subcategoryCode} but got 0 results`
        );
        console.log(`🔍 [SUBCATEGORY DEBUG] Search parameters sent:`, {
          layer: effectiveLayer,
          categoryCode: selectedCategory,
          subcategoryCode: selectedSubcategory,
        });
      }

      // Removed stale data checks - no caching needed

      // DEBUG: Log search query album art data
      if (process.env.NODE_ENV === 'development' && results.length > 0) {
        console.log(`🔍 SEARCH RAW Results (${results.length} total) - First 2 album art data:`, {
          selectedLayer: effectiveLayer,
          selectedCategory,
          selectedSubcategory,
          searchQuery: effectiveQuery
        });
        results.slice(0, 2).forEach((asset, index) => {
          console.log(`🖼️ SEARCH RAW Asset ${index + 1}:`, {
            id: asset.id || (asset as any)._id,
            albumArt: asset.albumArt,
            'songMetadata.albumArt': (asset as any).songMetadata?.albumArt,
            'songMetadata.albumArtUrl': (asset as any).songMetadata?.albumArtUrl,
            'metadata.albumArtUrl': asset.metadata?.albumArtUrl,
            'aiMetadata.layerMetadata.albumArt': asset.aiMetadata?.layerMetadata?.albumArt,
            songMetadataKeys: (asset as any).songMetadata ? Object.keys((asset as any).songMetadata) : 'No songMetadata'
          });
        });
      }

      // Normalize asset structure for frontend use (HFN from API humanFriendlyName only)
      const normalizedResults: Asset[] = results.map(asset => ({
        ...asset,
        id: asset.id || (asset as any)._id || (asset as any).assetId,
        layer:
          asset.layer || (asset as any).assetLayer || asset.metadata?.layer,
        humanFriendlyName: (asset as any).humanFriendlyName ?? '',
        machineFriendlyName: (asset as any).humanFriendlyName ?? '',
        friendlyName:
          asset.name ||
          asset.friendlyName ||
          (asset as any).humanFriendlyName ||
          (asset as any).hfn ||
          (asset as any).HFN,
      })) as Asset[];

      // DEBUG: Log search query album art data after normalization
      if (process.env.NODE_ENV === 'development' && normalizedResults.length > 0) {
        console.log(`🔄 SEARCH NORMALIZED Results - First 2 album art data:`);
        normalizedResults.slice(0, 2).forEach((asset, index) => {
          console.log(`🔄 SEARCH NORMALIZED Asset ${index + 1}:`, asset, {
            id: asset.id,
            albumArt: asset.albumArt,
            'songMetadata.albumArt': (asset as any).songMetadata?.albumArt,
            'songMetadata.albumArtUrl': (asset as any).songMetadata?.albumArtUrl,
            'metadata.albumArtUrl': asset.metadata?.albumArtUrl,
            'aiMetadata.layerMetadata.albumArt': asset.aiMetadata?.layerMetadata?.albumArt,
            songMetadataPreserved: !!(asset as any).songMetadata
          });
        });
      }

      // Apply settings-based asset filtering
      let filteredResults = normalizedResults;
      if (isFilterEnabled && hideAssetsBeforeDate) {
        const cutoffDate = new Date(`${hideAssetsBeforeDate}T00:00:00Z`);
        filteredResults = normalizedResults.filter(asset => {
          try {
            const createdAt = new Date(
              asset.createdAt ||
                asset.metadata?.createdAt ||
                (asset as any).created_at
            );
            return createdAt >= cutoffDate;
          } catch (error) {
            // If date parsing fails, keep the asset (fail open)
            return true;
          }
        });

        if (process.env.NODE_ENV === 'development') {
          console.log(
            `🧹 Settings-based filter: ${normalizedResults.length} → ${filteredResults.length} assets (hiding assets before ${hideAssetsBeforeDate})`
          );
        }
      }

      // Removed client-side filtering fallbacks - backend handles all filtering
      // Only keep excludeLayers filtering as it's a feature, not a fallback
      if (excludeLayers.length > 0) {
        filteredResults = filteredResults.filter(asset => {
          return !excludeLayers.includes(asset.layer || '');
        });
      }

      // Backend now handles filtering via category/subcategory parameters
      // Apply client-side sorting using shared sort function
      let sortedResults = filteredResults;
      if (sortBy && sortedResults.length > 0) {
        sortedResults = applySortToResults(sortedResults, sortBy, sortOrder);
      }

      // Handle pagination differently when sorting vs normal browsing
      if (isSortingActive && sortedResults.length > itemsPerPage) {
        // Client-side pagination for sorted results
        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginatedResults = sortedResults.slice(startIndex, endIndex);

        setSearchResults(paginatedResults);
        // PAGINATION FIX: Show backend total count when we got a full page and backend reports more
        const displayTotal =
          sortedResults.length >= itemsPerPage && totalCount > sortedResults.length
            ? totalCount
            : sortedResults.length;
        setTotalAssets(displayTotal);
        setCurrentPage(page);
        // Calculate total pages based on the display total to maintain correct pagination
        setTotalPages(Math.ceil(displayTotal / itemsPerPage));

        if (process.env.NODE_ENV === 'development') {
          console.log(
            `🔄 Client-side pagination: Showing ${startIndex + 1}-${Math.min(
              endIndex,
              sortedResults.length
            )} of ${displayTotal} total results (${
              sortedResults.length
            } loaded for sorting)`
          );
        }
      } else {
        // Normal server-side pagination - backend handles all filtering
        setSearchResults(sortedResults);
        setTotalAssets(totalCount);
        setCurrentPage(page);
        setTotalPages(Math.ceil(totalCount / itemsPerPage));
      }

      // Record performance metrics
      perfTimer.stop({
        searchType: page === 1 ? 'search' : 'pagination',
        query: effectiveQuery,
        layer: effectiveLayer,
        category: selectedCategory,
        subcategory: selectedSubcategory,
        resultCount: searchResults.length,
        apiCalls: 1, // One API call for search
        dataTransferred: JSON.stringify(searchResults).length, // Approximate bytes
        cacheHits: 0, // TODO: Get from cache service
        cacheMisses: 1, // TODO: Get from cache service
        clientSideFiltering: false, // Backend handles all filtering including gender filters (commit 4a34a66)
        serverSideFiltering: true, // Backend now handles filtering including Song layer gender filters
      });

      // Call the onSearch callback with the query
      onSearch?.(effectiveQuery);
    } catch (error) {
      console.error('Error searching assets:', error);
      setSearchResults([]);
      setTotalAssets(0);
      setTotalPages(1);

      // Record error performance metrics
      perfTimer.stop({
        searchType: page === 1 ? 'search' : 'pagination',
        query: effectiveQuery,
        layer: effectiveLayer,
        category: selectedCategory,
        subcategory: selectedSubcategory,
        resultCount: 0,
        apiCalls: 1,
        dataTransferred: 0,
        cacheHits: 0,
        cacheMisses: 1,
        clientSideFiltering: false,
        serverSideFiltering: true,
      });
    } finally {
      setIsLoading(false);
    }
  };


  // Shared sort function that can be used without re-fetching data
  const applySortToResults = (
    results: Asset[],
    currentSortBy: string,
    currentSortOrder: 'asc' | 'desc'
  ): Asset[] => {
    if (!currentSortBy || results.length === 0) return results;

    const LAYER_ORDER: Record<string, number> = {
      B: 1, // Branded
      C: 2, // Composites
      G: 3, // Songs
      L: 4, // Looks
      M: 5, // Moves
      P: 6, // Personalize
      R: 7, // Rights
      S: 8, // Stars
      T: 9, // Training_Data
      W: 10, // Worlds
    };

    return [...results].sort((a, b) => {
      let aValue: any = '';
      let bValue: any = '';

      switch (currentSortBy) {
        case 'createdAt':
          try {
            const aDate = a.createdAt ? new Date(a.createdAt) : new Date(0);
            const bDate = b.createdAt ? new Date(b.createdAt) : new Date(0);
            aValue = isNaN(aDate.getTime()) ? 0 : aDate.getTime();
            bValue = isNaN(bDate.getTime()) ? 0 : bDate.getTime();
          } catch (error) {
            aValue = 0;
            bValue = 0;
          }
          break;
        case 'updatedAt':
          try {
            const aUpdateDate = a.updatedAt
              ? new Date(a.updatedAt)
              : new Date(0);
            const bUpdateDate = b.updatedAt
              ? new Date(b.updatedAt)
              : new Date(0);
            aValue = isNaN(aUpdateDate.getTime()) ? 0 : aUpdateDate.getTime();
            bValue = isNaN(bUpdateDate.getTime()) ? 0 : bUpdateDate.getTime();
          } catch (error) {
            aValue = 0;
            bValue = 0;
          }
          break;
        case 'name':
          aValue = (a.name || a.friendlyName || '').toLowerCase();
          bValue = (b.name || b.friendlyName || '').toLowerCase();
          break;
        case 'layer':
          aValue = LAYER_ORDER[a.layer || ''] || 999;
          bValue = LAYER_ORDER[b.layer || ''] || 999;
          break;
        case 'createdBy':
          // Backend returns 'registeredBy' as primary field for creator information
          aValue = (
            (a as any).registeredBy ||
            a.createdBy ||
            a.metadata?.createdBy ||
            (a as any).created_by ||
            ''
          ).toLowerCase();
          bValue = (
            (b as any).registeredBy ||
            b.createdBy ||
            b.metadata?.createdBy ||
            (b as any).created_by ||
            ''
          ).toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return currentSortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return currentSortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    performSearch(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    performSearch(1);
  };

  // Removed handleForceRefresh - no cache busting needed

  const handleClearSearch = () => {
    setSearchQuery('');
    setSelectedLayer('G'); // Reset to default Songs layer
    setSelectedCategory('');
    setSelectedSubcategory('');
    setCurrentPage(1);
    // Clear search terms and suggestions
    setSearchTerms([]);
    setSearchSuggestions([]);
    // Clear enhanced search state
    setSearchType('GENERAL');
    setSearchMetadata(null);
    // NEW: Clear gender filters
    setGenderFilters({
      originalArtistGender: '',
      genderSuitability: '',
      vocalRange: '',
      ageAppropriateness: '',
    });
    // Immediate search to show all assets when clearing filters
    performSearch(1);
  };


  const handleLayerChange = (event: SelectChangeEvent<string>) => {
    const newLayer = event.target.value;
    setSelectedLayer(newLayer);
    setSelectedCategory('');
    setSelectedSubcategory('');
    // Search will trigger automatically via useEffect when selectedLayer changes
  };

  const handleCategoryChange = (event: SelectChangeEvent<string>) => {
    setSelectedCategory(event.target.value);
    setSelectedSubcategory('');
    // Search will trigger automatically via useEffect when selectedCategory changes
  };

  const handleSubcategoryChange = (event: SelectChangeEvent<string>) => {
    setSelectedSubcategory(event.target.value);
    // Search will trigger automatically via useEffect when selectedSubcategory changes
  };

  // Removed cache busting functions - no caching needed

  // Enhanced search term processing
  const processSearchTerms = (query: string): string[] => {
    if (!query.trim()) return [];

    // Split by common delimiters and clean up
    const terms = query
      .split(/[\s,;|]+/)
      .map(term => term.trim().toLowerCase())
      .filter(term => term.length > 0);

    return Array.from(new Set(terms)); // Remove duplicates
  };

  const updateSearchHistory = (query: string) => {
    if (!query.trim()) return;

    const newSearches = [
      query,
      ...recentSearches.filter(s => s !== query),
    ].slice(0, 5);
    setRecentSearches(newSearches);

    // Persist to localStorage
    localStorage.setItem('nna-recent-searches', JSON.stringify(newSearches));
  };

  const generateSearchSuggestions = (query: string) => {
    if (!query.trim() || query.length < 2) {
      setSearchSuggestions([]);
      return;
    }

    // Generate suggestions based on common search patterns
    const suggestions = [
      `${query} video`,
      `${query} asset`,
      `layer:${query}`,
      `tag:${query}`,
      `name:${query}`,
    ]
      .filter(suggestion => suggestion.toLowerCase() !== query.toLowerCase())
      .slice(0, 3);

    setSearchSuggestions(suggestions);
  };

  // Removed auto-search useEffect - search now only triggers on explicit user actions:
  // 1. Clicking Search button
  // 2. Changing layer (handleLayerChange)
  // 3. Changing category (handleCategoryChange)
  // 4. Changing subcategory (handleSubcategoryChange)

  // Load recent searches from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('nna-recent-searches');
    if (stored) {
      try {
        setRecentSearches(JSON.parse(stored));
      } catch (error) {
        console.warn('Failed to load recent searches:', error);
      }
    }
  }, []);

  // Load settings from localStorage on component mount
  useEffect(() => {
    try {
      const savedDate = localStorage.getItem('nna-hide-assets-before-date');
      const savedEnabled = localStorage.getItem('nna-hide-test-assets');

      if (savedDate) {
        setHideAssetsBeforeDate(savedDate);
      }

      if (savedEnabled !== null) {
        setIsFilterEnabled(JSON.parse(savedEnabled));
      }
    } catch (error) {
      console.warn('Failed to load settings:', error);
    }
  }, []);

  // Listen for settings changes (e.g. from other sources)
  useEffect(() => {
    const handleSettingsChange = (event: CustomEvent) => {
      const { hideAssetsBeforeDate, isEnabled } = event.detail;
      setHideAssetsBeforeDate(hideAssetsBeforeDate);
      setIsFilterEnabled(isEnabled);

      // Automatically refresh search results when settings change (only if we have results)
      if (
        searchResults.length > 0 ||
        searchQuery ||
        selectedLayer ||
        selectedCategory ||
        selectedSubcategory
      ) {
        performSearch(currentPage);
      }
    };

    window.addEventListener(
      'nna-settings-changed',
      handleSettingsChange as EventListener
    );

    return () => {
      window.removeEventListener(
        'nna-settings-changed',
        handleSettingsChange as EventListener
      );
    };
  }, [
    currentPage,
    searchResults.length,
    searchQuery,
    selectedLayer,
    selectedCategory,
    selectedSubcategory,
    selectedAssetType,
  ]);

  // Trigger search when sort parameters change (but not on initial load)
  useEffect(() => {
    if (initialLoad) {
      setInitialLoad(false);
      return;
    }
    // Trigger search when sort parameters change after initial load
    performSearch(currentPage);
  }, [sortBy, sortOrder]);

  // Removed periodic data freshness check - no caching needed

  // Automatic search when searchQuery changes (with debouncing)
  useEffect(() => {
    // Mark component as mounted after first render
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return; // Skip search on initial mount - existing initial load logic handles this
    }

    // Clear existing timeout
    if (searchDebounceTimeoutRef.current) {
      clearTimeout(searchDebounceTimeoutRef.current);
    }

    // Set new timeout for debounced search
    searchDebounceTimeoutRef.current = setTimeout(() => {
      // Update search history when search query changes
      if (searchQuery && searchQuery.trim()) {
        updateSearchHistory(searchQuery);
      }

      // When parent controls layer via searchParams, pass overrides so layer is correct
      if (searchParams?.layer !== undefined) {
        performSearch(1, { layer: searchParams.layer, query: searchQuery });
      } else {
        performSearch(1);
      }
    }, 400); // 400ms debounce delay

    // Cleanup timeout on unmount or when searchQuery changes
    return () => {
      if (searchDebounceTimeoutRef.current) {
        clearTimeout(searchDebounceTimeoutRef.current);
      }
    };
  }, [searchQuery]); // Trigger when searchQuery changes

  // Automatic search when layer changes (immediate, no debouncing)
  useEffect(() => {
    // When parent controls search via searchParams (e.g. component browse), only searchParams effect should drive search
    if (searchParams?.layer !== undefined) return;
    // Skip on initial mount - existing initial load logic handles this
    if (!hasMountedRef.current) {
      return;
    }

    // Trigger search immediately when layer changes
    performSearch(1);
  }, [selectedLayer]); // Trigger when selectedLayer changes

  // Automatic search when category changes (immediate, no debouncing)
  useEffect(() => {
    if (searchParams?.layer !== undefined) return;
    // Skip on initial mount or if no layer selected
    if (!hasMountedRef.current || !selectedLayer) {
      return;
    }

    // Trigger search immediately when category changes
    performSearch(1);
  }, [selectedCategory]); // Trigger when selectedCategory changes

  // Automatic search when subcategory changes (immediate, no debouncing)
  useEffect(() => {
    if (searchParams?.layer !== undefined) return;
    // Skip on initial mount or if no category selected
    if (!hasMountedRef.current || !selectedCategory) {
      return;
    }

    // Trigger search immediately when subcategory changes
    performSearch(1);
  }, [selectedSubcategory]); // Trigger when selectedSubcategory changes

  // Automatic search when asset type filter changes (immediate, no debouncing)
  useEffect(() => {
    if (searchParams?.layer !== undefined) return;
    // Skip on initial mount - existing initial load logic handles this
    if (!hasMountedRef.current) {
      return;
    }

    // Trigger search immediately when asset type filter changes
    performSearch(1);
  }, [selectedAssetType]); // Trigger when selectedAssetType changes

  // Search query handler - updates UI immediately, search triggers via useEffect
  const handleSearchQueryChange = (value: string) => {
    // Update search query immediately for responsive UI
    setSearchQuery(value);

    // Update search type detection immediately for responsive UI
    const detectedType = assetService.detectSearchType(value);
    setSearchType(detectedType);

    // Process search terms immediately
    const terms = processSearchTerms(value);
    setSearchTerms(terms);

    // Generate suggestions immediately
    generateSearchSuggestions(value);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {!hideSearchBox && (
        <Box sx={{ p: 3, mb: 4 }}>
          <Box sx={{ position: 'relative', mb: 2 }}>
            <TextField
              fullWidth
              placeholder={getSearchPlaceholder()}
              value={searchQuery}
              onChange={e => handleSearchQueryChange(e.target.value)}
              aria-label="Search assets"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        setSearchQuery('');
                        setSearchTerms([]);
                        setSearchSuggestions([]);
                      }}
                      edge="end"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              variant="outlined"
              size="medium"
              disabled={isLoading}
            />

            {/* Search Suggestions */}
            {searchSuggestions.length > 0 && (
              <Paper
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 1000,
                  mt: 0.5,
                  maxHeight: 200,
                  overflow: 'auto',
                }}
              >
                {searchSuggestions.map((suggestion, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 1.5,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                      borderBottom:
                        index < searchSuggestions.length - 1
                          ? '1px solid'
                          : 'none',
                      borderColor: 'divider',
                    }}
                    onClick={() => {
                      handleSearchQueryChange(suggestion);
                      setSearchSuggestions([]);
                    }}
                  >
                    <Typography variant="body2">{suggestion}</Typography>
                  </Box>
                ))}
              </Paper>
            )}
          </Box>

          {/* Search Terms Display */}
          {searchTerms.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mr: 1 }}
              >
                Search terms:
              </Typography>
              {searchTerms.map((term, index) => (
                <Chip
                  key={index}
                  label={term}
                  size="small"
                  variant="outlined"
                  sx={{ mr: 0.5, mb: 0.5 }}
                  onDelete={() => {
                    const newTerms = searchTerms.filter((_, i) => i !== index);
                    const newQuery = newTerms.join(' ');
                    handleSearchQueryChange(newQuery);
                  }}
                />
              ))}
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              mb: 1,
            }}
          >
            {(searchQuery ||
              selectedLayer ||
              selectedCategory ||
              selectedSubcategory) && (
              <Button
                color="secondary"
                onClick={handleClearSearch}
                size="small"
              >
                Clear All
              </Button>
            )}
          </Box>

          {/* Filters - always visible when not hidden by parent */}
          {!hideFilters && (
            <Box sx={{ mb: 1.5 }}>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Filters
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="layer-select-label">Layer</InputLabel>
                    <Select
                      labelId="layer-select-label"
                      id="layer-select"
                      value={selectedLayer}
                      label="Layer"
                      onChange={handleLayerChange}
                      aria-label="Filter by layer"
                    >
                      {layers.map(layer => (
                        <MenuItem key={layer.code} value={layer.code}>
                          {layer.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small" disabled={!selectedLayer}>
                    <InputLabel id="category-select-label">Category</InputLabel>
                    <Select
                      labelId="category-select-label"
                      id="category-select"
                      value={selectedCategory}
                      label="Category"
                      onChange={handleCategoryChange}
                      disabled={!selectedLayer || categories.length === 0}
                      aria-label="Filter by category"
                    >
                      <MenuItem value="">
                        <em>Any Category</em>
                      </MenuItem>
                      {categories.map(category => (
                        <MenuItem key={category.code} value={category.code}>
                          {category.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small" disabled={!selectedCategory}>
                    <InputLabel id="subcategory-select-label">
                      Subcategory
                    </InputLabel>
                    <Select
                      labelId="subcategory-select-label"
                      id="subcategory-select"
                      value={selectedSubcategory}
                      label="Subcategory"
                      onChange={handleSubcategoryChange}
                      disabled={!selectedCategory || subcategories.length === 0}
                      aria-label="Filter by subcategory"
                    >
                      <MenuItem value="">
                        <em>Any Subcategory</em>
                      </MenuItem>
                      {subcategories.map(subcategory => (
                        <MenuItem
                          key={subcategory.code}
                          value={subcategory.code}
                        >
                          {subcategory.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="asset-type-select-label">
                      Asset Type
                    </InputLabel>
                    <Select
                      labelId="asset-type-select-label"
                      id="asset-type-select"
                      value={selectedAssetType}
                      label="Asset Type"
                      onChange={e => setSelectedAssetType(e.target.value)}
                      aria-label="Filter by asset type"
                    >
                      <MenuItem value="all">
                        <em>All Assets</em>
                      </MenuItem>
                      <MenuItem value="base">Base Assets</MenuItem>
                      <MenuItem value="variant">Variant Assets</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          )}

        </Box>
      )}

      {/* Results section - either search results or recent assets */}
      {searchResults.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">
              {/* Show "Search Results" if a search was performed, otherwise "Recent Assets" */}
              {searchQuery ||
              selectedLayer ||
              selectedCategory ||
              selectedSubcategory
                ? 'Search Results'
                : 'Recent Assets'}
            </Typography>
            <Chip
              label={`${totalAssets} asset${totalAssets !== 1 ? 's' : ''} ${
                searchQuery ||
                selectedLayer ||
                selectedCategory ||
                selectedSubcategory
                  ? 'found'
                  : 'available'
              }`}
              color="primary"
              variant="outlined"
            />

            {/* Enhanced search type indicator */}
            {searchQuery && (searchType === 'HFN' || searchType === 'MFA') && (
              <Chip
                label={getSearchTypeBadgeLabel()}
                size="small"
                color={getSearchTypeBadgeColor() as any}
                variant="filled"
                sx={{ ml: 1 }}
              />
            )}

            {/* Search metadata display for enhanced searches */}
            {searchMetadata && searchMetadata.found > 0 && (
              <Chip
                label={`Found ${searchMetadata.found} with ${searchMetadata.searchType} search`}
                size="small"
                color="secondary"
                variant="outlined"
                sx={{ ml: 1 }}
              />
            )}
          </Box>
          <Grid container spacing={{ xs: 2, sm: 3, md: 3 }} sx={{ mt: 2 }}>
            {searchResults.map(asset => (
              <Grid
                item
                xs={12}
                sm={fullWidth ? 4 : 6}
                md={fullWidth ? 3 : 4}
                lg={fullWidth ? 2 : 3}
                xl={fullWidth ? 2 : 3}
                key={asset.id}
                sx={{
                  display: 'flex',
                  '& > *': {
                    width: '100%',
                  },
                }}
              >
                <AssetCard
                  asset={asset}
                  onAssetSelect={onAssetSelect}
                  showActions={!hideCardActions}
                  onCreateVariant={
                    VARIANT_LAYERS.includes(asset.layer || selectedLayer) &&
                    asset.baseAsset === true
                      ? a => {
                          navigate(`/assets/${a.id || a._id}/create-variant`);
                        }
                      : undefined
                  }
                />
              </Grid>
            ))}
          </Grid>

          {/* Pagination Controls - Show when we have results */}
          {totalAssets > 0 && (
            <PaginationControls
              page={currentPage}
              totalPages={totalPages}
              totalItems={totalAssets}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              itemsPerPageOptions={[6, 12, 24, 48]}
              disabled={isLoading}
            />
          )}
        </Box>
      )}

      {/* Loading indicator */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Unsupported layer message */}
      {layerUnsupportedMessage && !isLoading && (
        <Alert severity="info" sx={{ mt: 4 }}>
          {layerUnsupportedMessage}
        </Alert>
      )}

      {/* No results message */}
      {searchResults.length === 0 &&
        !layerUnsupportedMessage &&
        (searchQuery ||
          selectedLayer ||
          selectedCategory ||
          selectedSubcategory) &&
        !isLoading && (
          <Alert severity="info" sx={{ mt: 4 }}>
            No assets found for your search criteria.
          </Alert>
        )}
    </Box>
  );
};

export default AssetSearch;
